const withTheme = styles => theme => styles.map(style => typeof style === 'function' ? style(theme) : style);
const colors = {
  primaryLighter: '#D6E6F3',
  primaryLight: '#559AD4',
  primary: '#3281C4',
  primaryDark: '#284959',
  secondaryLight: '#7ECBCE',
  secondary: '#00868B',
  secondaryDark: '#2F4857',
  white: '#FFFFFF',
  greyLighter: '#F5F5F5',
  greyLight: '#DCE2E3',
  grey: '#9EAAB0',
  greyDark: '#6F777B',
  black: '#404040',
  warningLighter: '#FDF4E2',
  warningLight: '#FCEAC6',
  warning: '#FDCE77',
  focus: '#D67E19',
  errorBackground: '#FFE5E3',
  errorLighter: '#F9BDB9',
  errorLight: '#C6342A',
  error: '#B12F25'
};
const baseline = 16;
const layout = {
  baseline,
  lineHeight: 38,
  inputWidths: {
    small: baseline * 5,
    medium: baseline * 15,
    large: baseline * 25
  },
  tableColumnWidths: {
    medium: baseline * 15
  }
};
const borders = {
  radius: {
    low: '5px',
    medium: '15px',
    high: '20px'
  }
};
const shadows = {
  short: '0 2px 4px 0 rgba(0, 0, 0, 0.2)',
  medium: '0 2px 8px 0 rgba(158, 170, 176, 0.4)',
  long: '0 4px 20px 0 rgba(158, 170, 176, 0.5)'
};
const grid = {
  breakpoints: {
    s: 480,
    m: 640,
    l: 960
  },
  columns: {
    s: 6,
    m: 12,
    l: 12
  },
  gutter: {
    s: 15,
    m: 40,
    l: 40
  },
  maxWidth: {
    s: 500,
    m: 860,
    l: 1280
  }
};
const font = {
  family: {
    heading: '"Montserrat", Verdana, Geneva, sans-serif',
    body: '"Nunito", Arial, Helvetica, sans-serif'
  },
  baseline: '100%',
  scale: {
    // 12pt
    small: '0.75rem',
    // 14pt
    default: '0.875rem',
    // 16pt
    medium: '1rem',
    // 18pt
    large: '1.125rem',
    // 22pt
    'x-large': '1.375rem',
    // 28pt
    'xx-large': '1.75rem',
    // 34pt
    'xxx-large': '2.125rem'
  }
};
const helpers = {
  mq: (min, max) => max ? `@media (min-width: ${grid.breakpoints[min]}px) and (max-width: ${grid.breakpoints[max]}px)` : `@media (min-width: ${grid.breakpoints[min]}px)`,
  print: '@media print',
  supports: {
    grid: '@supports (display: grid)'
  }
};
const styles = {
  disabled: {
    backgroundColor: colors.greyLighter,
    color: colors.grey
  },
  focused: {
    outline: 'none',
    boxShadow: `0 0 2px 2px ${colors.focus}`,
    borderColor: 'transparent'
  },
  placeholder: {
    color: colors.grey
  },
  ghost: {
    border: `1px solid ${colors.grey}`
  },
  fieldRow: {
    display: 'flex',
    flexWrap: 'wrap',
    '> *': {
      marginRight: baseline,
      '&:last-of-type': {
        marginRight: 0
      }
    }
  },
  flexColumnGrow: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1
  },
  revealableContent: _ref => {
    let {
      visible,
      visibleMaxHeight = 2000
    } = _ref;
    return {
      maxHeight: 0,
      overflowY: 'hidden',
      margin: -4,
      padding: 4,
      width: 'calc(100% + 8px)',
      transition: 'max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      ...(visible && {
        maxHeight: visibleMaxHeight
      })
    };
  },
  rotatable: _ref2 => {
    let {
      rotated,
      rotation = 180
    } = _ref2;
    return {
      transition: 'transform 0.1s ease-out',
      ...(rotated && {
        transform: `rotate(${rotation}deg)`
      })
    };
  },
  grid: _ref3 => {
    let {
      gap,
      numColumns
    } = _ref3;
    return {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      marginBottom: -gap,
      '> *': {
        width: `calc((100% - ${(numColumns - 1) * gap}px) / ${numColumns})`,
        marginBottom: gap
      },
      [helpers.supports.grid]: {
        display: 'grid',
        gridTemplateColumns: '1fr '.repeat(numColumns),
        gap,
        marginBottom: 0,
        '> *': {
          width: 'auto',
          marginBottom: 0
        }
      }
    };
  }
};
var theme = {
  colors,
  styles,
  borders,
  shadows,
  layout,
  grid,
  font,
  helpers
};
export { theme as t, withTheme as w };